import { Box, FormControlLabel, Switch, TextField } from "@mui/material";
import { LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";

import { Fragment, useEffect, useRef, useState } from "react";
import whiteA from "@/shared/assets/whiteA.png";
import whiteB from "@/shared/assets/whiteB.png";
import blackA from "@/shared/assets/blackA.png";
import blackB from "@/shared/assets/blackB.png";
import L from "leaflet";
import { DraggableMarker } from "@/shared/maps/DraggableMarker";
import Geocoder from "@/shared/maps/Geocoder";
import { SitePosition as Position } from "../Project";
import { Coordinate, fallbackSite, readCoordinate, sameCoordinate, yardNearSite } from "./positionCoordinates";

const BoundsSetter = ({ boundRequest, positions }: { boundRequest: number; positions: Coordinate[] }) => {
  const map = useMap();
  const lastFit = useRef<{ key: string; request: number } | null>(null);
  useEffect(() => {
    const key = JSON.stringify(positions);
    if (lastFit.current?.key === key && lastFit.current.request === boundRequest) return;
    lastFit.current = { key, request: boundRequest };
    if (!positions.length) {
      map.setView([fallbackSite.lat, fallbackSite.lng], 10);
      return;
    }
    map.fitBounds(L.latLngBounds(positions), {
      maxZoom: Math.max(map.getZoom(), positions.length > 1 ? 17 : 13),
      paddingTopLeft: [50, 50],
      paddingBottomRight: [270, 50],
    });
  }, [map, boundRequest, positions]);
  return null;
};

export const PositionEditor = ({
  position,
  onChange,
}: {
  position: Position;
  onChange: (change: Partial<Position>) => any;
}) => {

  const site = readCoordinate(position.site);
  const yard = readCoordinate(position.yard);
  const mapPosition = site ?? fallbackSite;
  // Only a fallback generated in this open draft may move when the site arrives later.
  const provisionalYard = useRef<Coordinate | null>(null);

  const initialYardPosition = () => {
    if (yard && !sameCoordinate(yard, provisionalYard.current)) return position.yard;
    const next = yardNearSite(position.site);
    provisionalYard.current = site ? null : next;
    return next;
  };

  const [boundRequest, setBoundRequest] = useState(0);

  const setPosition = (pos, type) => {
    if (type === "yard") provisionalYard.current = null;
    onChange({
      ...position,
      [type]: pos,
    });
    forceSetBounds();
  };

  const bounds: Coordinate[] = site ? [site] : [];
  if (position.useYard && yard && !sameCoordinate(yard, provisionalYard.current)) bounds.push(yard);

  const forceSetBounds = () => {
    setBoundRequest(Math.random());
  };

  useEffect(() => {
    if (yard && provisionalYard.current && !sameCoordinate(yard, provisionalYard.current)) provisionalYard.current = null;
    if (position.useYard && (!yard || (site && sameCoordinate(yard, provisionalYard.current)))) {
      const next = yardNearSite(position.site);
      provisionalYard.current = site ? null : next;
      onChange({ ...position, yard: next });
    }
  }, [position, onChange, site, yard]);


  return (
    <Fragment>
      <MapContainer
        style={{
          width: "100%",
          height: "100%",
        }}
        center={[mapPosition.lat, mapPosition.lng]}
        zoom={10}
        scrollWheelZoom={true}
      >
        <BoundsSetter boundRequest={boundRequest} positions={bounds} />
        <LayersControl position="bottomleft">
          <LayersControl.BaseLayer checked name="Straßenkarte">
            <TileLayer
              attribution="&copy; HERE"
              url={
                "https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?lang=de&size=512&ppi=400&style=logistics.day&features=vehicle_restrictions:active_and_inactive&apiKey=pegZ-uUwbH06WeXyUQOIsCwaVv_m9u4wCYWAKKJmH6k"
              }
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellit">
            <TileLayer
              attribution="&copy; HERE"
              url={
                "https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?lang=de&size=512&ppi=400&style=explore.satellite.day&features=vehicle_restrictions:active_and_inactive&apiKey=pegZ-uUwbH06WeXyUQOIsCwaVv_m9u4wCYWAKKJmH6k"
              }
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <DraggableMarker
          noDragEffect
          color={"black"}
          letter={"A"}
          position={mapPosition}
          setPosition={(x) => setPosition(x, "site")}
          draggable={true}
        />
        {position.useYard ? (
          <DraggableMarker
            noDragEffect
            color={"white"}
            letter={"B"}
            position={yard ?? yardNearSite(position.site)}
            setPosition={(x) => setPosition(x, "yard")}
            draggable={true}
          />
        ) : null}
      </MapContainer>
      <Box
        sx={{
          zIndex: 1200,
          position: "absolute",
          right: "20px",
          top: "20px",
          width: "250px",
        }}
      >
        <Box
          sx={{
            padding: "10px",
            border: "1px solid rgba(0,0,0,0.25)",
            background: "#f9f9f9",
          }}
        >
          <Geocoder
            noPOIs
            setPosition={(site) => {
              provisionalYard.current = null;
              onChange({
                ...position,
                site,
                useYard: false,
                yard: { lat: 0, lng: 0 },
              });
              forceSetBounds();
            }}
          />
        </Box>
        <Box
          sx={{
            mt: "10px",
            border: "1px solid rgba(0,0,0,0.25)",
            background: "#f9f9f9",
          }}
        >
          <FormControlLabel
            sx={{ padding: "10px", py: 0 }}
            control={
              <Switch
                checked={position.useYard}
                onChange={(_, useYard) => {
                  onChange({
                    ...position,
                    ...(useYard ? { yard: initialYardPosition() } : {}),
                    useYard,
                  });
                  forceSetBounds();
                }}
              />
            }
            label="Lagerplatz"
          />
          {position.useYard ? (
            <Box
              sx={{
                padding: "5px 10px",
                borderTop: "1px solid #aaa",
                borderBottom: "1px solid #aaa",
                fontSize: "90%",
                background: "#eee",
                alignItems: "center",
                display: "grid",
                gridTemplateColumns: "30px auto",
                gridTemplateRows: "30px 30px",
                gap: "5px",
              }}
            >
              <Box
                sx={{
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "left center",
            
                  alignSelf: "stretch",
                  backgroundImage:
                    "url(" + (blackA) + ")",
                  backgroundSize: "contain",
                }}
              />
              <Box >
                Entladestelle
              </Box>
              <Box
            
                sx={{
                  backgroundRepeat: "no-repeat",
               
                  backgroundPosition: "left center",
                  alignSelf: "stretch",
                  backgroundImage:
                    "url(" + (whiteB ) + ")",
                  backgroundSize: "contain",
                }}
              />
              <Box>
                Lagerplatz
              </Box>
            </Box>
          ) : null}
          <FormControlLabel
            sx={{ padding: "10px", py: 0 }}
            control={
              <Switch
                checked={position.onlySolo}
                onChange={(_, onlySolo) => {
                  onChange({
                    ...position,
                    onlySolo,
                  });
                }}
              />
            }
            label="Nur Solo, kein Sattel"
          />
          <Box
            sx={{
              p: "5px",
              pt: position.comment.length ? "8px" : "5px",
              borderTop: "1px solid #aaa",
              background: "#eee",
            }}
          >
            <TextField
              label="Anfahrtshinweise"
              value={position.comment}
              onChange={(e) =>
                onChange({
                  ...position,
                  comment: (e.target as HTMLInputElement).value,
                })
              }
              multiline
              sx={{ background: "white" }}
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};
